import { css } from 'styled-components';

/**
 * The sub-nav row's shared geometry and the active row's colours, in one place.
 *
 * There are two nav implementations — `views/shared/navigation/styled-nav-components.tsx` (investor
 * tool, proto shell) and `views/dma-v2-view/shared/navigation.tsx` (DMA dashboard and settings) —
 * and until this file they carried byte-identical copies of this rule, so a repaint had to be made
 * twice or the two navs drifted. The mixed ramps this file replaced are what that drift looked like:
 * the fill had been moved to `--color__blue-100` while the label and glyph still read from the
 * legacy `--platform-blue-*` scale. Both files import from here now.
 *
 * The selectors are Primer's: `NavList.Item` renders the row as an `<a>` and takes `aria-current`
 * from the caller, so `[aria-current='page']` is the active row. The `li li` variant is for a row
 * inside a `NavList.SubNav`.
 *
 * The pair is `lilac-800` on `lilac-100` — PLT-6076, decided by Janne on four candidates rendered
 * side by side. It is the same geometry as the blue pill it replaces, recoloured. That pair is also
 * the categorical badge's, which `design/10-foundations.md` §"Every colour has one job" now says
 * outright, with the condition that keeps the two readable: the nav row is a full-width row in a
 * single-select rail with a leading icon, never an inline chip beside content. Read that rule before
 * reaching for this pair anywhere else.
 */
/**
 * The nav row's own box — its padding and its radius — shared for the same reason its colours are.
 *
 * The inline padding is load-bearing for the pill, which is why it is here rather than left to each
 * nav. A row is 36px tall — a 20px icon with `sm` above and below — so `--border-radius__full`
 * resolves to an 18px semicircle at each end. At the icon's top corner, 10px above the row's
 * centre line, that curve has only come 3px in from the row edge, so an `sm` inline padding leaves
 * about 5px between the curve and the glyph and the pill reads as if it were clipping the icon.
 * `lg` puts 13px there. Janne asked for this on the shipped lilac pill (PLT-6076); the DMA nav was
 * the one at `sm`, the investor nav already declared `lg`.
 *
 * The radius is here because the focus ring traces it. Primer leaves a row's `<a>` at `md` (6px)
 * and paints its own focus rule on the `<li>` instead, so a focused row used to ring as a
 * 6px-cornered rectangle over a fully-rounded hover pill — two different shapes on one row
 * (PLT-6696). `full` on the box makes the ring the same pill everything else on the row is.
 *
 * Applies to every row, not only the active one: the padding is what positions the label, so
 * changing it for the active row alone would shift the text sideways as you navigate. And to
 * every *kind* of row — a top-level link like the DMA dashboard's Home, a row inside a `SubNav`,
 * and a section's header. A shorter row is not exempt: Home is 32px, so its end radius is 16px
 * rather than 18px, and Primer's own padding left 4.5px of clearance there — the same defect, on
 * the row a user lands on first.
 */
export const navRowBox = css`
  padding: var(--space__sm) var(--space__lg);
  border-radius: var(--border-radius__full);
`;

export const navActiveStyles = css`
  li a[aria-current='page'],
  li li a[aria-current='page'] {
    background-color: var(--color__lilac-100);
    color: var(--color__lilac-800);
    border-radius: var(--border-radius__full);

    & svg {
      color: var(--color__lilac-800);
    }
  }

  /* Primer gives the selected item's <li> (data-active) its own grey "selected" background with a
     6px radius. Sitting behind the fully-rounded anchor pill, its squarer corners peek out as grey
     wedges. Keep only the pill. */
  li.prc-ActionList-ActionListItem-uq6I7[data-active],
  li.prc-ActionList-ActionListItem-uq6I7[data-active]:hover {
    background-color: transparent;
  }
`;
